import { Fragment, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  hbIf,
  hbStr,
  formatSpanValue,
  readGeneratedReport,
  reportCacheKey,
  subscribeGeneratedReports,
  type CachedGeneratedReport,
} from '../reportTabData';
import type { GeneratedTabPaneProps } from './GeneratedReportTabs';

/**
 * Phase 5e (builder B) — the order family + proposal, ported 1:1 from the
 * legacy tabs. Legacy reality per tab (all verified from source):
 *
 * sales_order / service_order / shipping_order — one identical LIVE
 * POST→socket→render-in-tab choreography (the phx_selection pattern):
 *  - generateReport (sales_order.js:85-116 = service_order.js:76-108 =
 *    shipping_order.js:120-151): POST '<name>' with the whole dispatch state;
 *    the $.ajax SUCCESS render()s the static shell (header + bare dotted
 *    preloader — NO generation-in-progress container, so the 4.5s fade has
 *    nothing to fade); the UNCOMMENTED Promise.all([fn['create.websocket'],
 *    req_task]) socket wait matches the celery result by task_id, stashes
 *    `self.data[state] = <message>` + the order-format timestamp under the
 *    2-part `current_report + project_id` key and renderReport()s the report
 *    template into `.content-item` (replacing the preloader). The kit's
 *    orderDispatch performs POST + socket wait and writes the reportTabData
 *    cache; these panes subscribe.
 *  - timer choreography: service_order/shipping_order return from
 *    updateReportsData right after the 'generate:report:<name>' trigger
 *    (action.js:115-118) — no pending rows, no 4.5s/5s timers, no
 *    Recent-Reports auto-open — so their rendered report STAYS up.
 *    sales_order gets the full treatment (rows + timers); its in-tab render
 *    is hidden by the 5s revert and returns through the reopen path.
 *  - 'open:generated_report:<name>' is LIVE for all three (sales_order.js:
 *    74-83, service_order.js:65-74, shipping_order.js:110-118) and their
 *    cache IS populated by the live socket leg → REOPENABLE_TABS entries;
 *    the cache key deliberately omits ahu_id (reportCacheKey).
 *  - renderReport extras: the `.integer` loop comma-groups values via
 *    fn.uni_tools.formatNumberWithCommaSeparator (sales_order.js:48-53 —
 *    only the sales template HAS .integer spans); sales also formatter-binds
 *    `span[data-formatter_type]` (decimal commissions, :64-70); its
 *    `.table-wrapper` scroll binding (:58-63) matches NO element (the
 *    template has rep-table-wrapper, not table-wrapper) — dead, not ported.
 *    service/shipping call formatDates() over `.date` elements — none exist
 *    in either template (only inside HTML comments) — no-op, not ported.
 *    The `$('.header-buttons button#print'/'#export_csv')` prop toggles are
 *    no-ops (main_header.tmpl renders neither button).
 *  - shipping renderReport (shipping_order.js:48-96) mutates the rows in
 *    place before the insert: when a row's actual_delivery_time OR
 *    actual_loading_time_complete is non-null AND any of six actual times is
 *    longer than 5 chars, 12 sched/actual time fields get .slice(0,-3)
 *    ('HH:MM:SS' → 'HH:MM'); shipping_terms gets its HTML tags stripped
 *    (:74-76). A null among the touched fields THROWS in legacy and aborts
 *    renderReport before the insert — transformShippingRows reproduces the
 *    exact access pattern and the caller catches, leaving the preloader.
 *  - shipping extras: 'click .shipping_terms_more' opens the shipping-terms
 *    modal APPENDED to #ksdm-content (shipping_order.js:98-108 +
 *    shipping_term_modal.tmpl) — repeated opens append additional instances
 *    and close only strips 'active' from all of them (both quirks kept);
 *    the reports-page body keyup ESC handler REMOVES active popup-wrappers
 *    (view/main.js:44-49) — reproduced locally since the shared usePopupKeys
 *    only clicks cancel-named buttons. The 'click #asd' → closeShippingModal
 *    events entry points at a method that does not exist — dead.
 *  - export_csv (sales_order.js:133-178 et al.): only reachable through the
 *    header's 'click #export_csv' (main_header.js:62) and main_header.tmpl
 *    renders no #export_csv button — unreachable, not ported (5c precedent).
 *  - template quirks kept: the effective DOM of service/shipping's UNCLOSED
 *    </h3> (the parser closes it at the container-header </div>); shipping's
 *    `<h6 class="page-title">…</h3>` parses as a closed h6; the page-navbar
 *    labels' for="shipping_order_<id>" point at the labels' OWN ids (the
 *    checkboxes are id="order_<id>") so the per-page filter is inert — in
 *    the SERVICE template too (copy-paste); class typos 'outputs-contalier ',
 *    'col-xs-offset2', 'shippinp-print-data', "Manuacturer's Price", the
 *    'shipping-table-rows ' trailing / ' reps-outputs-table' leading class
 *    spaces, and shipping's Ship row re-reading sched_loading_date.
 *
 * proposal — a generate-only shell (the 5c family), NOT the socket flow:
 *  - the LIVE generateReport is proposal.js:164-193 (the socket/sub-view one
 *    at :93-162 is fully commented out): delete ahu_id when falsy/0, add the
 *    real mfg_id, POST 'proposal/export' (DOCX via the notifications poll);
 *    SUCCESS → render() the static shell only.
 *  - render() (:57-66) sets sectionName = self.current_state, which NO live
 *    code path ever assigns → undefined → the group tooltip renders ": " and
 *    the report-name is the template's hardcoded "Proposal"
 *    (proposal.tmpl:3). The proposal_format loop is entirely inside an HTML
 *    comment (proposal.tmpl:14-29) — Handlebars still evaluates it but the
 *    output is an invisible comment node — so the initialize-time PRP
 *    structure-fields fetch (:48-54) has no visible surface (the kit fires
 *    the GET once for network parity).
 *  - ALL sub-machinery is dead: renderReport (:68-83) + report.project.tmpl
 *    (100% commented out) only run from openGeneratedReport when
 *    `this.data[state]` exists, and live code never writes this.data → NOT
 *    reopenable (the page default); the ViewPriceSummary /
 *    ViewEquipmentList / ViewEquipmentDescription sub-views (and with them
 *    the /api/v1.0/reports/equipment_description/<project_id> feeder and the
 *    'equipment_list' celery POSTs) are only instantiated inside the
 *    commented-out generateReport; 'export_csv:report:proposal' is bound to
 *    this.export_doc which DOES NOT EXIST (Backbone silently skips);
 *    model/mapping.js is unrequired (and a syntax error if it ever were).
 *  - template quirks kept: the double space in "content-item  full-height"
 *    (proposal.tmpl:9) and the empty time-stamp span (never filled —
 *    setTimeStamp only runs from the dead renderReport).
 */

type Row = Record<string, unknown>;

/** Handlebars path lookup — a missing intermediate resolves to undefined
 *  (→ '' through hbStr), never a throw. */
function path(o: unknown, ...keys: string[]): unknown {
  return keys.reduce<unknown>((acc, k) => (acc == null ? undefined : (acc as Row)[k]), o);
}

/** fn.uni_tools.formatNumberWithCommaSeparator (universal_fn_tools.js:973-977). */
function commaSeparated(raw: string): string {
  if (Number.isNaN(Number(raw))) return raw;
  const formatted = String(Number(raw)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return formatted.includes(',') ? formatted : raw;
}

/** The renderReport `.integer` loop (sales_order.js:48-53 + validate
 *  :126-131): falsy text stays untouched. */
function intText(v: unknown): string {
  const text = hbStr(v);
  return text ? commaSeparated(text) : text;
}

/** The cached socket result for this pane — reopen key in reopen mode, the
 *  run's dispatch state otherwise (single dispatch: project-scope reports
 *  never fan out), keyed by the 2-part order key (reportCacheKey). */
function useOrderEntry({
  mode,
  states,
  reopenKey,
}: Pick<GeneratedTabPaneProps, 'mode' | 'states' | 'reopenKey'>):
  | CachedGeneratedReport
  | undefined {
  const generateMode = mode !== 'reopen';
  const key = useMemo(() => {
    if (!generateMode) return reopenKey ?? null;
    const last = states && states.length ? states[states.length - 1] : null;
    return last ? reportCacheKey(last) : null;
  }, [generateMode, states, reopenKey]);

  const [entry, setEntry] = useState<CachedGeneratedReport | undefined>(() =>
    key && !generateMode ? readGeneratedReport(key) : undefined
  );
  // Generate mode starts EMPTY even when a previous run's result is cached
  // under the same key: the legacy render() wipes the tab to its shell and
  // renderReport only runs from THIS run's socket handler (or the reopen
  // path) — so only a write newer than the dispatch-time baseline renders.
  // `states` is in the deps so a re-generate over a live pane re-baselines.
  useEffect(() => {
    if (!key) return undefined;
    const baseline = generateMode ? readGeneratedReport(key) : undefined;
    setEntry(generateMode ? undefined : readGeneratedReport(key));
    return subscribeGeneratedReports(() => {
      const e = readGeneratedReport(key);
      if (!generateMode || e !== baseline) setEntry(e);
    });
  }, [key, generateMode, states]);
  return entry;
}

// ---- Sales Order ----

/** `self.data[state].data.sales_order_data` (sales_order.js:44) — ONE object
 *  (SalesSerializer(sales).data, tasks/reports_tasks/sales_order_project.py:
 *  25,41). Missing → legacy renderReport throws before the insert → null
 *  keeps the preloader. */
function extractSales(entry: CachedGeneratedReport | undefined): Row | null {
  const data = entry?.message?.data as { sales_order_data?: Row } | undefined;
  if (!data || data.sales_order_data == null) return null;
  return data.sales_order_data;
}

/** tabs/sales_order/tmpl/report.project.tmpl:1-232, rendered into
 *  `.content-item` (sales_order.js:47). */
const SalesOrderReport = ({ d }: { d: Row }) => {
  const shipping = (path(d, 'project', 'shipping') as Row[] | undefined) ?? [];
  const reps = (d.rep_commission as Row[] | undefined) ?? [];
  return (
    <>
      <div className="report-outputs-group-wrapper">
        <div className="row top-group-info">
          <div className="">
            <div className="col-xs-5">
              <div className="row">
                <div className="col-xs-3">
                  <span className="name">Customer</span>
                </div>
                <div className="col-xs-9">
                  <span className="output">
                    {hbIf(path(d, 'project', 'customer_co'))
                      ? hbStr(path(d, 'project', 'customer_co', 'name'))
                      : ''}
                  </span>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-3">
                  <span className="name">Contact</span>
                </div>
                <div className="col-xs-9">
                  <span className="output">{hbStr(path(d, 'project', 'customer_contact', 'name'))}</span>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-3">
                  <span className="name">Address</span>
                </div>
                <div className="col-xs-9">
                  {hbIf(path(d, 'project', 'customer_co')) && (
                    <>
                      <div className="row">
                        <div className="col-xs-12">
                          <span className="output">
                            {hbStr(path(d, 'project', 'customer_co', 'address_line_one'))}
                          </span>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-xs-12">
                          <span className="output">
                            {hbStr(path(d, 'project', 'customer_co', 'address_line_two'))}
                          </span>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-xs-12">
                          <span className="output">
                            <span>{hbStr(path(d, 'project', 'customer_co', 'city'))}</span>{' '}
                            <span>{hbStr(path(d, 'project', 'customer_co', 'state'))}</span>{' '}
                            <span>{hbStr(path(d, 'project', 'customer_co', 'postal_code'))}</span>{' '}
                            <span>{hbStr(path(d, 'project', 'customer_co', 'country'))}</span>
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-xs-3">
                  <span className="name">Office</span>
                </div>
                <div className="col-xs-9">
                  <span className="output">{hbStr(path(d, 'project', 'customer_co', 'tel_office'))}</span>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-3">
                  <span className="name">Mobile</span>
                </div>
                <div className="col-xs-9">
                  <span className="output">
                    {hbStr(path(d, 'project', 'customer_contact', 'tel_mobile'))}
                  </span>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-3">
                  <span className="name">Email</span>
                </div>
                <div className="col-xs-9">
                  <span className="output">{hbStr(path(d, 'project', 'customer_contact', 'email'))}</span>
                </div>
              </div>
            </div>
            <div className="col-xs-7">
              <div className="row">
                <div className="col-xs-5">
                  <span className="name">Purchase Order No.</span>
                </div>
                <div className="col-xs-6 col-xs-offset-1">
                  <span className="output">{hbStr(d.po_number)}</span>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-5">
                  <span className="name">Sale Amount</span>
                </div>
                <div className="col-xs-1 text-right no-right-padding">
                  <span className="unit">US$</span>
                </div>
                <div className="col-xs-6">
                  <span className="output integer">{intText(d.sell_amount_to_customer)}</span>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-5">
                  {/* "Manuacturer's" [sic] — legacy typo kept (tmpl:64). */}
                  <span className="name">Manuacturer&#39;s Price</span>
                </div>
                <div className="col-xs-1 text-right no-right-padding">
                  <span className="unit">US$</span>
                </div>
                <div className="col-xs-6">
                  <span className="output integer">{intText(d.mfg_price_actual)}</span>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-5">
                  <span className="name">Quote Number</span>
                </div>
                <div className="col-xs-6 col-xs-offset-1">
                  <span className="output">{hbStr(path(d, 'project', 'quote_number'))}</span>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-5">
                  <span className="name">Sale Type</span>
                </div>
                <div className="col-xs-1 text-right no-right-padding">
                  <span className="unit" />
                </div>
                <div className="col-xs-6">
                  <span className="output">{hbStr(d.sale_type)}</span>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-5">
                  <span className="name">Total Commission</span>
                </div>
                <div className="col-xs-1 text-right no-right-padding">
                  <span className="unit">US$</span>
                </div>
                <div className="col-xs-6">
                  <span className="output integer">{intText(d.total_rep_commission)}</span>
                </div>
              </div>
              {/* tmpl:82 carries a DUPLICATE class attribute (class="row"
                  class="top-margin") — the browser keeps the first. */}
              <div className="row">
                <div className="col-xs-5">
                  <span className="name">Task : Status</span>
                </div>
                <div className="col-xs-6 col-xs-offset-1">
                  <span className="output">Order : Start</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="row bottom-group-info">
          <div className="">
            <div className="col-xs-5 top-margin">
              <div className="row">
                <div className="col-xs-3">
                  <span className="name">Owner</span>
                </div>
                <div className="col-xs-9">
                  <span className="output">{hbStr(path(d, 'project', 'customer_co', 'name'))} </span>{' '}
                  <span className="output second-output">
                    {hbStr(path(d, 'project', 'customer_contact', 'name'))}
                  </span>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-3">
                  <span className="name">Lender</span>
                </div>
                <div className="col-xs-9">
                  <span className="output">
                    {hbStr(path(d, 'project', 'construction_lender_co', 'name'))}{' '}
                  </span>{' '}
                  <span className="output second-output">
                    {hbStr(path(d, 'project', 'construction_lender_contact', 'name'))}
                  </span>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-3">
                  <span className="name">Bonder</span>
                </div>
                <div className="col-xs-9">
                  <span className="output">
                    {hbStr(path(d, 'project', 'construction_bonder_co', 'name'))}{' '}
                  </span>{' '}
                  <span className="output second-output">
                    {hbStr(path(d, 'project', 'construction_bonder_contact', 'name'))}
                  </span>
                </div>
              </div>
            </div>
            <div className="col-xs-7 top-margin">
              <div className="row">
                <div className="col-xs-6">
                  <span className="name">General Contractor</span>
                </div>
                <div className="col-xs-6">
                  <span className="output">
                    {hbStr(path(d, 'project', 'general_contractor_co', 'name'))}{' '}
                  </span>{' '}
                  <span className="output second-output">
                    {hbStr(path(d, 'project', 'general_contractor_contact', 'name'))}
                  </span>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-6">
                  <span className="name">Mechanical Contractor</span>
                </div>
                <div className="col-xs-6">
                  <span className="output">
                    {hbStr(path(d, 'project', 'mechanical_contractor_co', 'name'))}{' '}
                  </span>{' '}
                  <span className="output second-output">
                    {hbStr(path(d, 'project', 'mechanical_contractor_contact', 'name'))}
                  </span>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-6">
                  <span className="name">Electrical Contractor</span>
                </div>
                <div className="col-xs-6">
                  <span className="output">
                    {hbStr(path(d, 'project', 'electrical_contractor_co', 'name'))}{' '}
                  </span>{' '}
                  <span className="output second-output">
                    {hbStr(path(d, 'project', 'electrical_contractor_contact', 'name'))}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="rep-table-wrapper col-xs-12 print-rep-content">
        <div className="">
          <div className="shipping-table-heading row">
            <div className="col-xs-3 row header-name">
              <h3 className="header">Equipment</h3>
            </div>
            <div className="col-xs-3 row header-name">
              <h3 className="header">SHIPPING : SCHED | ACTUAL</h3>
            </div>
            <div className="col-xs-3 row header-name">
              <h3 className="header">DELIVERY : SCHED | ACTUAL</h3>
            </div>
            <div className="col-xs-3 row header-name">
              <h3 className="header">Address</h3>
            </div>
          </div>
        </div>
        {/* trailing class space — tmpl:165 verbatim. */}
        <div className="shipping-table-rows ">
          {shipping.map((s, i) => (
            <div className="row every-row" key={i}>
              {/* 'outputs-contalier ' [sic] — legacy typo + trailing space. */}
              <div className="col-xs-3 row outputs-contalier ">
                <span className="output">{hbStr(s.equipment_for_delivery)}</span>
              </div>
              <div className="col-xs-3 row outputs-contalier">
                <span className="output">
                  {hbStr(s.sched_ship_date)}
                  {hbIf(s.sched_ship_date) && hbIf(s.actual_ship_date) ? ' | ' : ''}
                  {hbStr(s.actual_ship_date)}
                </span>
              </div>
              <div className="col-xs-3 row outputs-contalier">
                <span className="output">
                  {hbStr(s.sched_delivery_date)}
                  {hbIf(s.sched_delivery_date) && hbIf(s.actual_delivery_date) ? ' | ' : ''}
                  {hbStr(s.actual_delivery_date)}
                </span>
              </div>
              <div className="col-xs-3 row outputs-contalier">
                <span className="outputs">
                  {hbIf(s.delivery_delivery_address_line_1) && (
                    <span className="output table-output">
                      {hbStr(s.delivery_delivery_address_line_1)}
                    </span>
                  )}{' '}
                  {hbIf(s.delivery_delivery_address_line_2) && (
                    <span className="output table-output">
                      {hbStr(s.delivery_delivery_address_line_2)}
                    </span>
                  )}{' '}
                  {hbIf(s.delivery_address_city) && (
                    <span className="output table-output">
                      {/* inner spans are ADJACENT in the template (tmpl:174). */}
                      {hbIf(s.delivery_address_city) && <span>{hbStr(s.delivery_address_city)}</span>}
                      {hbIf(s.delivery_address_st) && <span>{hbStr(s.delivery_address_st)}</span>}
                      {hbIf(s.delivery_address_postal_code) && (
                        <span>{hbStr(s.delivery_address_postal_code)}</span>
                      )}
                      {hbIf(s.delivery_address_country) && (
                        <span>{hbStr(s.delivery_address_country)}</span>
                      )}
                    </span>
                  )}{' '}
                  {hbIf(s.delivery_co_contact_person) && (
                    <span className="output table-output">{hbStr(s.delivery_co_contact_person)}</span>
                  )}{' '}
                  {hbIf(s.delivery_contact_tel_office) && (
                    <span className="output table-output">{hbStr(s.delivery_contact_tel_office)}</span>
                  )}{' '}
                  {hbIf(s.delivery_contact_tel_mobile) && (
                    <span className="output table-output">
                      {hbIf(s.delivery_contact_tel_mobile) && (
                        <span>{hbStr(s.delivery_contact_tel_mobile)}</span>
                      )}
                      {hbIf(s.delivery_contact_email) && <span>{hbStr(s.delivery_contact_email)}</span>}
                    </span>
                  )}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* leading class space — tmpl:184 verbatim. */}
      <div className=" reps-outputs-table">
        <div className="top-margin col-lg-10 col-md-10 col-sm-10 col-xs-12">
          <div className="sales-table-heading row">
            <div className="col-xs-8">
              <h3 className="header row">Representative</h3>
            </div>
            <div className="col-xs-4 text-right">
              <div className="row">
                <h3 className="header">Commission</h3>
              </div>
            </div>
          </div>
        </div>
        <div className="sales-table-rows col-lg-10 col-md-10 col-sm-10 col-xs-12">
          <div className="row">
            <div className="col-xs-7">
              <div className="row label-content">
                <span>
                  {hbStr(path(d, 'project', 'ordering_rep_co', 'name'))}{' '}
                  {hbIf(path(d, 'project', 'ordering_rep_contact', 'name')) ? ' , ' : ''}{' '}
                </span>{' '}
                <span>{hbStr(path(d, 'project', 'ordering_rep_contact', 'name'))}</span>
              </div>
            </div>
            <div className="col-xs-5 text-right">
              <div className="row">
                <div className="col-lg-2 col-md-2 col-sm-2 col-xs-2 text-right no-right-padding">
                  <span className="unit">%</span>
                </div>
                <div className="col-lg-2 col-md-2 col-sm-2 col-xs-3 no-right-padding">
                  <span className="output" data-formatter_type="decimal">
                    {formatSpanValue(hbStr(d.ordering_rep_co_commission_percent), 'decimal')}
                  </span>
                </div>
                <div className="col-lg-2 col-md-2 col-sm-2 col-xs-2 text-right no-right-padding">
                  <span className="unit">US$</span>
                </div>
                <div className="col-lg-6 col-md-6 col-sm-6 col-xs-5 no-right-padding">
                  <span className="output integer">{intText(d.ordering_rep_co_commission_amount)}</span>
                </div>
              </div>
            </div>
          </div>
          {reps.map((r, i) => (
            <div className="row" key={i}>
              <div className="col-xs-7">
                <div className="row label-content">
                  <span>
                    {hbStr(r.rep_co)} {hbIf(r.rep_contact_person) ? ' , ' : ''}
                  </span>
                  <span> {hbStr(r.rep_contact_person)} </span>
                </div>
              </div>
              <div className="col-xs-5 text-right">
                <div className="row">
                  <div className="col-lg-2 col-md-2 col-sm-2 col-xs-2 text-right no-right-padding">
                    <span className="unit">%</span>
                  </div>
                  <div className="col-lg-2 col-md-2 col-sm-2 col-xs-3 no-right-padding">
                    <span className="output" data-formatter_type="decimal">
                      {formatSpanValue(hbStr(r.commission_percent), 'decimal')}
                    </span>
                  </div>
                  <div className="col-lg-2 col-md-2 col-sm-2 col-xs-2 text-right no-right-padding">
                    <span className="unit">US$</span>
                  </div>
                  <div className="col-lg-6 col-md-6 col-sm-6 col-xs-5 no-right-padding">
                    <span className="output integer">{intText(r.rep_co_commission_amount)}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

/** tabs/sales_order/tmpl/sales_order.tmpl:1-12 shell; report renders into the
 *  content-item, replacing the dotted preloader (sales_order.js:47). */
export const SalesOrderTab = ({ report, rendered, mode, states, reopenKey }: GeneratedTabPaneProps) => {
  const entry = useOrderEntry({ mode, states, reopenKey });
  const d = useMemo(() => extractSales(entry), [entry]);
  const showShell = mode === 'reopen' || rendered;
  return (
    <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
      {showShell && (
        <>
          <div className="container-header clearfix">
            <h3 className="left">
              {/* sectionName = the .current_report menu button text
                  (sales_order.js:36). */}
              <span className="group-name" tooltip={`: ${report.name}`}>
                Sales
              </span>
              <span className="report-name">{report.name}</span>{' '}
              <span className="time-stamp">{d && entry ? `Generated on: ${entry.timeStamp}` : ''}</span>
            </h3>
          </div>
          <div className="report-content col-xs-12">
            <div className="content col-xs-12">
              <div className="content-item col-xs-12">
                {d ? <SalesOrderReport d={d} /> : <div className="preloader dotted-preloader active" />}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ---- Service Order ----

/** `{services: self.data[state].data.service_order_data}` (service_order.js:
 *  44-46) — an array (ProjectServiceSerializer many=True). message.data
 *  missing → legacy throws pre-insert (preloader stays); the KEY missing →
 *  legacy renders the empty form (each over undefined). */
function extractService(entry: CachedGeneratedReport | undefined): Row[] | null {
  const data = entry?.message?.data as { service_order_data?: Row[] } | undefined;
  if (!data) return null;
  return Array.isArray(data.service_order_data) ? data.service_order_data : [];
}

/** The mfg city/state/postal/country span run shared by the service and
 *  shipping report templates (service report.project.tmpl:68-71 = shipping
 *  :61-64): ", "-suffixed spans, whitespace inside each #if block. */
const MfgCitySpans = ({ s }: { s: Row }) => (
  <span className="">
    {hbIf(s.mfg_city) && (
      <>
        <span>{hbStr(s.mfg_city)}, </span>{' '}
      </>
    )}
    {hbIf(s.mfg_state) && (
      <>
        <span>{hbStr(s.mfg_state)}, </span>{' '}
      </>
    )}
    {hbIf(s.mfg_postal_code) && (
      <>
        <span>{hbStr(s.mfg_postal_code)}, </span>{' '}
      </>
    )}
    {hbIf(s.mfg_country) && <span>{hbStr(s.mfg_country)}</span>}
  </span>
);

/** The page-navbar block both order-report templates append after the pages
 *  (service report.project.tmpl:270-284 = shipping :331-345). The labels'
 *  for/id BOTH say shipping_order_<id> (the checkboxes are order_<id>) — the
 *  filter is inert, faithfully. */
const OrderPageNavbar = ({ rows }: { rows: Row[] }) => (
  <div className="col-xs-6 inputs fan-report v-group-inputs">
    <div className="multiple-select-wrapper row">
      <div className="page-navbar-wrapper list-inline">
        <button
          id="show_available_sections"
          type="button"
          name="mounting_plane_list"
          className="col-xs-2 icon-hamburger ka-has-tooltip page-navbar-menu-btn multiple-select-ctrl-btn"
          tooltip="All List"
          tabIndex={-1}
        />
        <ul className="page-navbar-menu">
          <li>
            <span className="all-label page-ctrl-label active" id="all_reports_to_show">
              All
            </span>
          </li>
          {rows.map((s, i) => (
            <li key={i}>
              <label
                htmlFor={`shipping_order_${hbStr(s.id)}`}
                id={`shipping_order_${hbStr(s.id)}`}
                className="page-ctrl-label"
              >
                {hbStr(s.order_title)}
              </label>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

/** tabs/service_order/tmpl/report.project.tmpl:1-287. */
const ServiceOrderReport = ({ rows }: { rows: Row[] }) => (
  <form>
    <div className="pages-container">
      {rows.map((s, i) => (
        <Fragment key={i}>
          <input
            id={`order_${hbStr(s.id)}`}
            type="checkbox"
            name="page_ctrl_btn"
            className="page-ctrl-btn hidden"
            defaultChecked
          />
          <div className={`col-xs-12 container-page report-per-page shipping_order_${hbStr(s.id)}`}>
            <div className="col-xs-12">
              <div className="title">
                <h3 className="page-title">
                  <span className="label">Title</span>
                  {hbIf(s.order_title) ? hbStr(s.order_title) : '- - -'}
                </h3>
              </div>
              <div className="row">
                <div className="col-xs-12">
                  <div className="col-xs-12">
                    <div className="row">
                      <div className="col-md-6 col-xs-6">
                        <div className="row">
                          <div className="">
                            <div className="row">
                              <div className="col-xs-4">
                                <div className="label">Order No.</div>
                              </div>
                              <div className="col-xs-8">
                                <div className="output">{hbStr(s.order_number)}</div>
                              </div>
                            </div>
                          </div>
                          <div className="">
                            <div className="row">
                              <div className="col-xs-4">
                                <div className="label">Description</div>
                              </div>
                              <div className="col-xs-8">
                                <div className="output">{hbStr(s.order_description)}</div>
                              </div>
                            </div>
                          </div>
                          <div className="">
                            <div className="row">
                              <div className="col-xs-4">
                                <div className="label">By</div>
                              </div>
                              <div className="col-xs-8 with-padding">
                                <div className="output">
                                  {hbStr(s.order_created_by_first_name)}{' '}
                                  {hbStr(s.order_created_by_last_name)}
                                </div>
                              </div>
                              <div className="col-xs-offset-4 col-xs-8 with-padding">
                                <div className="output">{hbStr(s.mfg_address_line_1)}</div>
                              </div>
                              <div className="col-xs-offset-4 col-xs-8 with-padding">
                                <div className="output">{hbStr(s.mfg_address_line_2)}</div>
                              </div>
                              <div className="col-xs-offset-4 col-xs-8 with-padding">
                                <div className="output">
                                  <MfgCitySpans s={s} />
                                </div>
                              </div>
                              <div className="col-xs-offset-4 col-xs-8 with-padding">
                                <div className="output">{hbStr(s.mfg_tel_office)}</div>
                              </div>
                              <div className="col-xs-offset-4 col-xs-8 with-padding">
                                <div className="output">{hbStr(s.mfg_email)}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6 col-xs-6">
                        <div className="with-padding">
                          <div className="with-padding">
                            <div className="row container_parts_rows">
                              <div className="col-xs-offset-4 col-xs-2">
                                <div className="label text-uppercase header-to-left-3 time-label-width-for-print">
                                  Scheduled
                                </div>
                              </div>
                              <div className="col-xs-offset-2 col-xs-2">
                                <div className="label text-uppercase header-to-left-3 time-label-width-for-print">
                                  Actual
                                </div>
                              </div>
                            </div>
                            <div className="row container_parts_rows">
                              <div className="col-xs-4 label">Work Start</div>
                              <div className="col-xs-2 output time-width-for-print">
                                {hbStr(s.sched_service_start_date)}
                              </div>
                              <div className="col-xs-2 output time-width-for-print">
                                {hbStr(s.sched_service_start_time)}
                              </div>
                              <div className="col-xs-2 output time-width-for-print">
                                {hbStr(s.actual_service_start_date)}
                              </div>
                              <div className="col-xs-2 output time-width-for-print">
                                {hbStr(s.actual_service_start_time)}
                              </div>
                            </div>
                            <div className="row container_parts_rows">
                              <div className="col-xs-4 label">Work Complete</div>
                              <div className="col-xs-2 output time-width-for-print">
                                {hbStr(s.sched_service_complete_date)}
                              </div>
                              <div className="col-xs-2 output time-width-for-print">
                                {hbStr(s.sched_service_complete_time)}
                              </div>
                              <div className="col-xs-2 output time-width-for-print">
                                {hbStr(s.actual_service_complete_date)}
                              </div>
                              <div className="col-xs-2 output time-width-for-print">
                                {hbStr(s.actual_service_complete_time)}
                              </div>
                            </div>
                            <div className="row container_parts_rows">
                              <div className="col-xs-4 label">Total Time / Trips</div>
                              <div className="col-xs-2 output">
                                {hbStr(s.sched_total_time)}{' '}
                                {hbIf(s.sched_total_time) && <span className="for-print">/ </span>}
                              </div>
                              <div className="col-xs-2 output">{hbStr(s.sched_total_trips)} </div>
                              <div className="col-xs-2 output">
                                {hbStr(s.actual_total_time)}{' '}
                                {hbIf(s.actual_total_time) && <span className="for-print">/ </span>}
                              </div>
                              <div className="col-xs-2 output">{hbStr(s.actual_total_trips)}</div>
                            </div>
                            <div className="row container_parts_rows">
                              <div className="col-xs-4 label">Cost</div>
                              <div className="col-xs-2 output">{hbStr(s.sched_cost)}</div>
                              {/* 'col-xs-offset2' [sic] — tmpl:131. */}
                              <div className="col-xs-offset2 col-xs-2 output">
                                {hbStr(s.actual_cost)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row" style={{ marginTop: '30px' }}>
                <div className="col-xs-12">
                  <div className="col-md-6 col-xs-6">
                    <div className="row">
                      <div className="with-padding">
                        <div className="row">
                          <div className="col-xs-4">
                            <div className="label">Equipment</div>
                          </div>
                          <div className="col-xs-8">
                            <div className="output">{hbStr(s.equipment_for_service)}</div>
                          </div>
                        </div>
                      </div>
                      <div className="with-padding">
                        <div className="row">
                          <div className="col-xs-4">
                            <div className="label">Service Co.</div>
                          </div>
                          <div className="col-xs-8">
                            <div className="output">{hbStr(s.service_co)}</div>
                          </div>
                        </div>
                      </div>
                      <div className="with-padding">
                        <div className="row">
                          <div className="col-xs-4">
                            <div className="label">Contact</div>
                          </div>
                          <div className="col-xs-8">
                            <div className="output">{hbStr(s.service_co_contact)}</div>
                          </div>
                        </div>
                      </div>
                      <div className="with-padding">
                        <div className="row">
                          <div className="col-xs-4">
                            <div className="label">Service Technician</div>
                          </div>
                          <div className="col-xs-8">
                            <div className="output">
                              <div className="with-padding">{hbStr(s.service_tech)}</div>
                              <div className="with-padding">{hbStr(s.service_tech_mob)}</div>
                              <div className="with-padding">{hbStr(s.service_tech_tel_office)}</div>
                              <div>{hbStr(s.service_tech_email)}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-xs-12 with-padding">
                        <div className="row">
                          <div className="col-xs-offset-4 col-xs-8">
                            <div className="output">{hbStr(s.freight_co_driver_mob)}</div>
                          </div>
                        </div>
                      </div>
                      <div className="col-xs-12 with-padding">
                        <div className="row">
                          <div className="col-xs-offset-4 col-xs-8">
                            <div className="output">{hbStr(s.freight_co_driver_email)}</div>
                          </div>
                        </div>
                      </div>
                      <div className="with-padding">
                        <div className="row">
                          <div className="col-xs-4">
                            <div className="label">Instructions</div>
                          </div>
                          <div className="col-xs-8">
                            <div className="output multi-line">{hbStr(s.instructions)}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 col-xs-6">
                    <div className="with-padding">
                      <div className="row">
                        <div className="row">
                          <div className="col-xs-4">
                            <div className="label">Location</div>
                          </div>
                          <div className="col-xs-8">
                            <div className="output">
                              <div className="with-padding">{hbStr(s.service_address_line_1)}</div>
                              <div className="with-padding">{hbStr(s.service_address_line_2)}</div>
                              <div className="with-padding">
                                {hbIf(s.service_address_city) && (
                                  <>
                                    <div className="with-padding">{hbStr(s.service_address_city)}</div>{' '}
                                  </>
                                )}
                                {hbIf(s.service_address_st) && (
                                  <>
                                    <div className="with-padding">{hbStr(s.service_address_st)}</div>{' '}
                                  </>
                                )}
                                {hbIf(s.service_address_postal_code) && (
                                  <>
                                    <div className="with-padding">
                                      {hbStr(s.service_address_postal_code)}
                                    </div>{' '}
                                  </>
                                )}
                                {hbIf(s.service_address_country) && (
                                  <div className="with-padding">{hbStr(s.service_address_country)}</div>
                                )}
                              </div>
                              <div className="with-padding">{hbStr(s.service_co_contact_person)}</div>
                              <div className="with-padding">{hbStr(s.service_contact_tel_office)}</div>
                              <div className="with-padding">{hbStr(s.service_contact_tel_mobile)}</div>
                              <div>{hbStr(s.service_contact_email)}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Fragment>
      ))}
      <OrderPageNavbar rows={rows} />
    </div>
  </form>
);

/** tabs/service_order/tmpl/service_order.tmpl:1-12 shell (its unclosed </h3>
 *  parses to the same effective DOM as a closed one). */
export const ServiceOrderTab = ({
  report,
  rendered,
  mode,
  states,
  reopenKey,
}: GeneratedTabPaneProps) => {
  const entry = useOrderEntry({ mode, states, reopenKey });
  const rows = useMemo(() => extractService(entry), [entry]);
  const showShell = mode === 'reopen' || rendered;
  return (
    <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
      {showShell && (
        <>
          <div className="container-header clearfix">
            <h3 className="left">
              <span className="group-name" tooltip={`: ${report.name}`}>
                Service
              </span>
              <span className="report-name">{report.name}</span>{' '}
              <span className="time-stamp">
                {rows && entry ? `Generated on: ${entry.timeStamp}` : ''}
              </span>
            </h3>
          </div>
          <div className="col-xs-12 content-container">
            <div className="content">
              <div className="content-item content-with-page-navbar">
                {rows ? (
                  <ServiceOrderReport rows={rows} />
                ) : (
                  <div className="preloader dotted-preloader active" />
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ---- Shipping Order ----

/**
 * renderReport's in-place row mutation (shipping_order.js:54-79), access
 * pattern preserved EXACTLY: a null among the touched fields throws (like
 * legacy) and the caller aborts the render; the length>5 guard makes the
 * mutation idempotent across re-renders of the same cached rows.
 */
function transformShippingRows(rows: Row[]): void {
  rows.forEach((row) => {
    const r = row as Record<string, string | null>;
    if (r.actual_delivery_time != null || r.actual_loading_time_complete != null) {
      if (
        (r.actual_delivery_time as string).length > 5 ||
        (r.actual_loading_time_complete as string).length > 5 ||
        (r.actual_loading_time_start as string).length > 5 ||
        (r.actual_off_load_time_complete as string).length > 5 ||
        (r.actual_off_load_time_start as string).length > 5 ||
        (r.actual_ship_time as string).length > 5
      ) {
        r.actual_delivery_time = (r.actual_delivery_time as string).slice(0, -3);
        r.actual_loading_time_complete = (r.actual_loading_time_complete as string).slice(0, -3);
        r.actual_loading_time_start = (r.actual_loading_time_start as string).slice(0, -3);
        r.actual_off_load_time_complete = (r.actual_off_load_time_complete as string).slice(0, -3);
        r.actual_off_load_time_start = (r.actual_off_load_time_start as string).slice(0, -3);
        r.actual_ship_time = (r.actual_ship_time as string).slice(0, -3);

        r.sched_delivery_time = (r.sched_delivery_time as string).slice(0, -3);
        r.sched_loading_time_complete = (r.sched_loading_time_complete as string).slice(0, -3);
        r.sched_loading_time_start = (r.sched_loading_time_start as string).slice(0, -3);
        r.sched_off_load_time_complete = (r.sched_off_load_time_complete as string).slice(0, -3);
        r.sched_off_load_time_start = (r.sched_off_load_time_start as string).slice(0, -3);

        r.sched_ship_time = (r.sched_ship_time as string).slice(0, -3);
      }
    }
    if (r.shipping_terms) {
      r.shipping_terms = (r.shipping_terms as string).replace(/(<([^>]+)>)/gi, '');
    }
  });
}

/** tabs/shipping_order/tmpl/report.project.tmpl (live part, :1-349). */
const ShippingOrderReport = ({
  rows,
  onOpenTerms,
}: {
  rows: Row[];
  onOpenTerms: (index: number) => void;
}) => (
  <form>
    <div className="pages-container">
      {rows.map((s, i) => (
        <Fragment key={i}>
          <input
            id={`order_${hbStr(s.id)}`}
            type="checkbox"
            name="page_ctrl_btn"
            className="page-ctrl-btn hidden"
            defaultChecked
          />
          <div className={`col-xs-12 container-page report-per-page shipping_order_${hbStr(s.id)}`}>
            <div className="col-xs-12">
              <div className="title">
                {/* tmpl:8 opens <h6> and closes </h3> — the parser closes the
                    h6, so the effective element is a closed h6. */}
                <h6 className="page-title">
                  <span className="label">Title</span>
                  {hbIf(s.order_title) ? hbStr(s.order_title) : '- - -'}
                </h6>
              </div>
              <div className="row">
                <div className="col-xs-12">
                  <div className="row">
                    <div className="col-md-5 col-xs-5">
                      <div className="row">
                        <div className="col-xs-3">
                          <div className="label">Order No.</div>
                        </div>
                        <div className="col-xs-9">
                          <div className="output">{hbStr(s.order_number)}</div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-xs-3">
                          <div className="label">Description</div>
                        </div>
                        <div className="col-xs-9">
                          <div className="output">{hbStr(s.order_description)}</div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-xs-3">
                          <div className="label">By</div>
                        </div>
                        <div className="col-xs-9">
                          <div className="output">
                            {hbStr(s.order_created_by_first_name)}{' '}
                            {hbStr(s.order_created_by_last_name)}
                          </div>
                        </div>
                        <div className="col-xs-offset-3 col-xs-9">
                          <div className="output">{hbStr(s.mfg_address_line_1)}</div>
                        </div>
                        <div className="col-xs-offset-3 col-xs-9">
                          <div className="output">{hbStr(s.mfg_address_line_2)}</div>
                        </div>
                        <div className="col-xs-offset-3 col-xs-9">
                          <div className="output">
                            <MfgCitySpans s={s} />
                          </div>
                        </div>
                        <div className="col-xs-offset-3 col-xs-9">
                          <div className="output">{hbStr(s.mfg_tel_office)}</div>
                        </div>
                        <div className="col-xs-offset-3 col-xs-9">
                          <div className="output">{hbStr(s.mfg_email)}</div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-7 col-xs-7">
                      <div className="row container_parts_rows">
                        <div className="col-xs-offset-1 col-xs-2">
                          <div className="text-uppercase label header-to-left-1">Scheduled</div>
                        </div>
                        <div className="col-xs-1" />
                        <div className="col-xs-1" />
                        <div className="col-xs-1" />
                        <div className="col-xs-2 to-left">
                          <div className="text-uppercase label header-to-left-2">Actual</div>
                        </div>
                      </div>
                      <div className="row container_parts_rows">
                        <div className="col-xs-1 label">Load</div>
                        <div className="col-xs-2 output">{hbStr(s.sched_loading_date)}</div>
                        <div className="col-xs-1 output">{hbStr(s.sched_loading_time_start)}</div>
                        <div className="col-xs-1 text-center">to</div>
                        <div className="col-xs-1 output">{hbStr(s.sched_loading_time_complete)}</div>
                        <div className="col-xs-2 output">{hbStr(s.actual_loading_date)}</div>
                        <div className="col-xs-1 text-center">to</div>
                        <div className="col-xs-1 output">{hbStr(s.actual_loading_time_start)}</div>
                        <div className="col-xs-2 output">{hbStr(s.actual_loading_time_complete)}</div>
                      </div>
                      <div className="row container_parts_rows">
                        <div className="col-xs-1 label">Ship</div>
                        {/* sched_loading_date again — legacy copy-paste bug
                            kept (tmpl:104). */}
                        <div className="col-xs-2 output">{hbStr(s.sched_loading_date)}</div>
                        <div className="col-xs-1 output">{hbStr(s.sched_ship_time)}</div>
                        <div className="col-xs-1 text-center" />
                        <div className="col-xs-1 output" />
                        <div className="col-xs-2 output">{hbStr(s.actual_ship_date)}</div>
                        <div className="col-xs-1 text-center" />
                        <div className="col-xs-1 output">{hbStr(s.actual_ship_time)}</div>
                        <div className="col-xs-2 output" />
                      </div>
                      <div className="row container_parts_rows">
                        <div className="col-xs-1 label">Deliver</div>
                        <div className="col-xs-2 output">{hbStr(s.sched_delivery_date)}</div>
                        <div className="col-xs-1 output">{hbStr(s.sched_delivery_time)}</div>
                        <div className="col-xs-1 text-center" />
                        <div className="col-xs-1 output" />
                        <div className="col-xs-2 output">{hbStr(s.actual_delivery_date)}</div>
                        <div className="col-xs-1 text-center" />
                        <div className="col-xs-1 output">{hbStr(s.actual_delivery_time)}</div>
                        <div className="col-xs-2 output" />
                      </div>
                      <div className="row container_parts_rows">
                        <div className="col-xs-1 label">Off-Load</div>
                        <div className="col-xs-2 output">{hbStr(s.sched_off_load_date)}</div>
                        <div className="col-xs-1 output">{hbStr(s.sched_off_load_time_start)}</div>
                        <div className="col-xs-1 text-center">to</div>
                        <div className="col-xs-1 output">{hbStr(s.sched_off_load_time_complete)}</div>
                        <div className="col-xs-2 output">{hbStr(s.actual_off_load_date)}</div>
                        <div className="col-xs-1 text-center">to</div>
                        <div className="col-xs-1 output">{hbStr(s.actual_off_load_time_start)}</div>
                        <div className="col-xs-2 output">{hbStr(s.actual_off_load_time_complete)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row page-break" style={{ marginTop: '30px' }}>
                <div className="col-xs-12">
                  <div className="row">
                    <div className="col-md-5 col-xs-5">
                      <div className="row">
                        <div className="col-xs-3">
                          <div className="label">Equipment</div>
                        </div>
                        <div className="col-xs-9">
                          <div className="output">{hbStr(s.equipment_for_delivery)}</div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-xs-3">
                          <div className="label">Freight Co.</div>
                        </div>
                        <div className="col-xs-9">
                          <div className="output">{hbStr(s.freight_co)}</div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-xs-3">
                          <div className="label">Contact</div>
                        </div>
                        <div className="col-xs-9">
                          <div className="output">{hbStr(s.freight_co_contact)}</div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-xs-3">
                          <div className="label">Driver</div>
                        </div>
                        <div className="col-xs-9">
                          <div className="output">{hbStr(s.freight_co_driver)}</div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-xs-offset-3 col-xs-9">
                          <div className="output">{hbStr(s.freight_co_driver_mob)}</div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-xs-offset-3 col-xs-9">
                          <div className="output">{hbStr(s.freight_co_driver_email)}</div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-xs-3">
                          <div className="label">Truck info</div>
                        </div>
                        <div className="col-xs-9">
                          <div className="output">{hbStr(s.type_qty_trucks)}</div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-xs-3">
                          <div className="label">Instructions</div>
                        </div>
                        <div className="col-xs-9">
                          <div className="output">{hbStr(s.instructions)}</div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-7 col-xs-7">
                      <div className="row">
                        <div className="col-md-6 col-xs-4 label">Delivery Address</div>
                        <div className="col-md-6 col-xs-8">
                          <div className="output">{hbStr(s.delivery_delivery_address_line_1)}</div>
                        </div>
                      </div>
                      {[
                        s.delivery_delivery_address_line_2,
                        s.delivery_address_city,
                        s.delivery_address_st,
                        s.delivery_address_postal_code,
                        s.delivery_address_country,
                        s.delivery_co_contact_person,
                        s.delivery_contact_tel_office,
                        s.delivery_contact_tel_mobile,
                        s.delivery_contact_email,
                      ].map((v, j) => (
                        <div className="row" key={j}>
                          {/* the double spaces inside this class run are
                              verbatim (tmpl:243 et seq.). */}
                          <div className="col-xs-offset-4 col-md-offset-6   col-md-6 col-xs-8">
                            <div className="output">{hbStr(v)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              {/* 'shippinp-print-data' [sic] — tmpl:309. */}
              <div className="row top-margin shippinp-print-data">
                <div className="col-xs-1 terms-disable-for-print">
                  <div className="label">Terms</div>
                </div>
                <div className="col-xs-4 shipping_for_print">
                  {hbIf(s.shipping_terms) && (
                    <>
                      <div
                        className="output left"
                        style={{ width: '78%', height: '20px', overflow: 'hidden' }}
                      >
                        {hbStr(s.shipping_terms)}
                      </div>
                      <div
                        className="shipping_terms_more"
                        data-index={i}
                        onClick={() => onOpenTerms(i)}
                      >
                        ...
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Fragment>
      ))}
      <OrderPageNavbar rows={rows} />
    </div>
  </form>
);

interface TermModal {
  text: string;
  active: boolean;
}

/** tabs/shipping_order/tmpl/shipping_order.tmpl:1-13 shell + the
 *  shipping-terms modal machinery (shipping_order.js:98-108). */
export const ShippingOrderTab = ({
  report,
  rendered,
  mode,
  states,
  reopenKey,
}: GeneratedTabPaneProps) => {
  const entry = useOrderEntry({ mode, states, reopenKey });
  const rows = useMemo(() => {
    const data = entry?.message?.data as { shipping_order_data?: Row[] } | undefined;
    if (!data) return null;
    const raw = data.shipping_order_data;
    // `if (self.dataArray)` (shipping_order.js:55) — no array, no transform;
    // the template still renders (an empty form).
    if (!Array.isArray(raw)) return [];
    try {
      transformShippingRows(raw);
    } catch {
      // Legacy renderReport throws before the html() insert — the pane keeps
      // its dotted preloader.
      return null;
    }
    return raw;
  }, [entry]);

  // Appended modal instances (legacy appends a new .shipping_term_modal to
  // #ksdm-content per '...' click; close strips 'active' from ALL of them but
  // leaves them in the DOM — both quirks kept).
  const [termModals, setTermModals] = useState<TermModal[]>([]);
  const openTerms = (index: number) => {
    const text = hbStr((rows ?? [])[index]?.shipping_terms);
    setTermModals((list) => [...list, { text, active: true }]);
  };
  const closeTerms = () =>
    setTermModals((list) => list.map((m) => ({ ...m, active: false })));

  // The reports page's body keyup ESC removes ACTIVE popup-wrappers from the
  // DOM (view/main.js:44-49); the shared usePopupKeys only clicks cancel-named
  // buttons (the modal has none), so reproduce the removal here.
  const anyActive = termModals.some((m) => m.active);
  useEffect(() => {
    if (!anyActive) return undefined;
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setTermModals((list) => list.filter((m) => !m.active));
    };
    document.addEventListener('keyup', onKeyUp);
    return () => document.removeEventListener('keyup', onKeyUp);
  }, [anyActive]);

  const showShell = mode === 'reopen' || rendered;
  const modalHost = document.getElementById('ksdm-content');
  return (
    <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
      {showShell && (
        <>
          <div className="container-header clearfix">
            <h3 className="left">
              <span className="group-name" tooltip={`: ${report.name}`}>
                Shipping
              </span>
              <span className="report-name"> {report.name}</span>{' '}
              <span className="time-stamp">
                {rows && entry ? `Generated on: ${entry.timeStamp}` : ''}
              </span>
            </h3>
          </div>
          <div className="col-xs-12 content-container">
            <div className="content">
              <div className="content-item content-with-page-navbar">
                {rows ? (
                  <ShippingOrderReport rows={rows} onOpenTerms={openTerms} />
                ) : (
                  <div className="preloader dotted-preloader active" />
                )}
              </div>
            </div>
          </div>
        </>
      )}
      {termModals.length > 0 &&
        modalHost &&
        createPortal(
          <>
            {termModals.map((m, i) => (
              // shipping_term_modal.tmpl:1-20 (duplicate inner id across
              // instances is faithful).
              <div
                key={i}
                className={`shipping_term_modal popup-wrapper${m.active ? ' active' : ''}`}
              >
                <div id="shipping_term_modal">
                  <div className="popup-header col-xs-12">
                    <div className="">
                      Shipping Terms
                      <button
                        className="icon-close-1 close_popup right"
                        tabIndex={-1}
                        onClick={closeTerms}
                      />
                    </div>
                  </div>
                  {/* double class space — tmpl:9 verbatim. */}
                  <div className="popup-body  popup-content__body col-xs-12">
                    <div className="col-xs-12">{m.text}</div>
                  </div>
                  <div className="popup-footer col-xs-12">
                    <div className="col-xs-12 popup-control-buttons full-height">
                      <button className="close_popup" name="close_popup" onClick={closeTerms}>
                        CLOSE
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </>,
          modalHost
        )}
    </div>
  );
};

// ---- Proposal ----

/** tabs/proposal/tmpl/proposal.tmpl:1-33 — a generate-only shell: group
 *  tooltip ": " (sectionName is undefined in every live path), hardcoded
 *  "Proposal" report-name, empty time-stamp, and the proposal_format loop
 *  lives inside an HTML comment (invisible). The DOCX arrives through the
 *  notifications poll. */
export const ProposalTab = ({ report, faded, rendered }: GeneratedTabPaneProps) => (
  <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
    {rendered && (
      <>
        <div className="container-header clearfix">
          <h3 className="left">
            <span className="group-name" tooltip=": ">
              Sales
            </span>
            <span className="report-name">Proposal</span>{' '}
            <span className="time-stamp" />
          </h3>
        </div>
        <div className="report-content col-xs-12">
          <div className="content">
            {/* double class space — proposal.tmpl:9 verbatim. */}
            <div className="content-item  full-height" style={{ overflow: 'auto' }}>
              <div
                className={`preloader-container generation-in-progress${faded ? ' opacity-0' : ''}`}
              >
                <div className="preloader active">Report generation in progress...</div>
              </div>
              <div id="proposal_structure" className="row full-height" />
            </div>
          </div>
        </div>
      </>
    )}
  </div>
);
